import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect.js";
import { transport } from "@/lib/grpc";
import { type WorkspaceSettings, useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
} from "react";
import {
	INITIAL_STATE,
	ONBOARDING_STEPS,
	type OnboardingState,
} from "./constants";

const wsClient = createClient(WorkspaceService, transport);

interface OnboardingContextValue {
	state: OnboardingState;
	isActive: boolean;
	hasStarted: boolean;
	currentStep: number;
	totalSteps: number;
	mode: OnboardingState["mode"];
	chatThreadId: string | undefined;
	advance: () => void;
	start: (mode?: "stepper" | "chat", chatThreadId?: string) => void;
	completeStep: (
		key: keyof Omit<
			OnboardingState,
			"step" | "completedAt" | "dismissed" | "mode" | "chatThreadId"
		>,
		nextStep?: number,
	) => void;
	dismiss: () => void;
	reset: () => void;
}

const Ctx = createContext<OnboardingContextValue | null>(null);

function readState(
	settings: WorkspaceSettings,
): OnboardingState & { _exists: boolean } {
	const raw = settings?.onboarding;
	if (!raw || typeof raw !== "object")
		return { ...INITIAL_STATE, _exists: false };
	return { ...INITIAL_STATE, ...(raw as OnboardingState), _exists: true };
}

function persist(wsId: string, next: OnboardingState | undefined) {
	const ws = useWorkspaceStore.getState().workspaces.find((w) => w.id === wsId);
	if (!ws) return;
	const merged = { ...ws.settings, onboarding: next };
	if (next === undefined) merged.onboarding = undefined;
	// Optimistic local update — immediately visible to all subscribers
	useWorkspaceStore.setState((s) => ({
		workspaces: s.workspaces.map((w) =>
			w.id === wsId ? { ...w, settings: merged } : w,
		),
	}));
	// Persist to backend in background
	import("@bufbuild/protobuf")
		.then(({ Struct }) =>
			wsClient.updateWorkspace({
				id: wsId,
				settings: Struct.fromJson(
					merged as Record<string, unknown> as {
						[key: string]: import("@bufbuild/protobuf").JsonValue;
					},
				),
			}),
		)
		.catch((e) => console.error("Failed to persist onboarding state", e));
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
	const ws = useWorkspaceStore((s) => s.selected());
	const stateWithMeta = useMemo(
		() => readState(ws?.settings ?? {}),
		[ws?.settings],
	);
	const { _exists, ...state } = stateWithMeta;
	// Only active if onboarding was explicitly started (settings.onboarding exists)
	const isActive = _exists && !state.dismissed && !state.completedAt && !!ws;

	const completeStep = useCallback(
		(
			key: keyof Omit<
				OnboardingState,
				"step" | "completedAt" | "dismissed" | "mode" | "chatThreadId"
			>,
			nextStep?: number,
		) => {
			if (!ws) return;
			const cur = readState(ws.settings);
			const next: OnboardingState = { ...cur, [key]: true };
			if (nextStep !== undefined) next.step = nextStep;
			else if (next.step < ONBOARDING_STEPS.length) next.step = cur.step + 1;
			persist(ws.id, next);
		},
		[ws],
	);

	const advance = useCallback(() => {
		if (!ws) return;
		const cur = readState(ws.settings);
		if (cur.step >= ONBOARDING_STEPS.length - 1) {
			persist(ws.id, { ...cur, completedAt: new Date().toISOString() });
		} else {
			persist(ws.id, { ...cur, step: cur.step + 1 });
		}
	}, [ws]);

	const start = useCallback(
		(mode?: "stepper" | "chat", chatThreadId?: string) => {
			if (!ws) return;
			persist(ws.id, {
				...INITIAL_STATE,
				mode: mode ?? "stepper",
				chatThreadId,
			});
		},
		[ws],
	);

	const dismiss = useCallback(() => {
		if (!ws) return;
		const cur = readState(ws.settings);
		persist(ws.id, { ...cur, dismissed: true });
	}, [ws]);

	const reset = useCallback(() => {
		if (!ws) return;
		persist(ws.id, undefined);
	}, [ws]);

	const value = useMemo<OnboardingContextValue>(
		() => ({
			state,
			isActive,
			hasStarted: _exists,
			currentStep: state.step,
			totalSteps: ONBOARDING_STEPS.length,
			mode: state.mode,
			chatThreadId: state.chatThreadId,
			advance,
			start,
			completeStep,
			dismiss,
			reset,
		}),
		[state, isActive, _exists, advance, start, completeStep, dismiss, reset],
	);

	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOnboarding() {
	const ctx = useContext(Ctx);
	if (!ctx)
		throw new Error("useOnboarding must be used within OnboardingProvider");
	return ctx;
}
